class TimezoneHours extends HTMLElement {
  constructor() {
    super();
    this.convertAndDisplay = this.convertAndDisplay.bind(this);
  }

  connectedCallback() {
    this.convertAndDisplay();
  }

  convertAndDisplay() {
    const openingHour = parseInt(this.getAttribute('opening-hour'), 10);
    const closingHour = parseInt(this.getAttribute('closing-hour'), 10);
    const storeTimezone = this.getAttribute('store-timezone') || 'Europe/Belgrade';

    try {
      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Create date objects for today with the store's hours
      const today = new Date();
      const storeOpeningDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        openingHour,
        0,
        0
      );
      const storeClosingDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        closingHour,
        0,
        0
      );

      // Convert store timezone hours to UTC
      const storeOpeningUTC = this.convertToUTC(storeOpeningDate, storeTimezone);
      const storeClosingUTC = this.convertToUTC(storeClosingDate, storeTimezone);

      // Convert UTC to user's timezone
      const userOpening = this.convertFromUTC(storeOpeningUTC, userTimezone);
      const userClosing = this.convertFromUTC(storeClosingUTC, userTimezone);

      // Format times
      const openingDisplay = this.formatTime(userOpening);
      const closingDisplay = this.formatTime(userClosing);

      // Format timezone (replace underscores with spaces)
      const formattedTimezone = userTimezone.replace(/_/g, ' ');

      // Update display with converted times and user's timezone
      this.innerHTML = `${openingDisplay} - ${closingDisplay}<br>${formattedTimezone}`;
    } catch (error) {
      // Fallback: show original times with store timezone
      console.error('Timezone conversion failed:', error);
      this.showFallback(openingHour, closingHour, storeTimezone);
    }
  }

  convertToUTC(date, timezone) {
    // Get the offset difference between the given timezone and UTC
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const getValue = (type) => parts.find((p) => p.type === type)?.value;

    const tzDate = new Date(
      getValue('year'),
      getValue('month') - 1,
      getValue('day'),
      getValue('hour'),
      getValue('minute'),
      getValue('second')
    );

    // Calculate offset
    const offset = tzDate.getTime() - date.getTime();
    return new Date(date.getTime() - offset);
  }

  convertFromUTC(utcDate, timezone) {
    // Convert UTC date to target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(utcDate);
    const getValue = (type) => parts.find((p) => p.type === type)?.value;

    return new Date(
      getValue('year'),
      getValue('month') - 1,
      getValue('day'),
      getValue('hour'),
      getValue('minute'),
      getValue('second')
    );
  }

  formatTime(date) {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';

    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hours}:${minutesStr}${ampm}`;
  }

  showFallback(openingHour, closingHour, storeTimezone) {
    const formatHour = (hour) => {
      const ampm = hour >= 12 ? 'pm' : 'am';
      let displayHour = hour % 12;
      displayHour = displayHour ? displayHour : 12;
      return `${displayHour}:00${ampm}`;
    };

    const openingDisplay = formatHour(openingHour);
    const closingDisplay = formatHour(closingHour);

    // Format timezone (replace underscores with spaces)
    const formattedTimezone = storeTimezone.replace(/_/g, ' ');

    this.innerHTML = `${openingDisplay} - ${closingDisplay}<br>${formattedTimezone}`;
  }
}

customElements.define('timezone-hours', TimezoneHours);
